import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Upload, Paperclip, Play, CheckCircle, FileText, Clock, AlertCircle,
  CalendarDays, TrendingUp, History, ChevronDown, ChevronUp, Send, RotateCcw,
  Sparkles, AlertTriangle, ExternalLink, Award, CheckCircle2, ShieldAlert,
  ArrowRight, RefreshCw, Layers, Download, Globe, Video, Briefcase
} from 'lucide-react';
import { FaGithub } from 'react-icons/fa';
import { SiFigma, SiGoogledrive, SiJira } from 'react-icons/si';
import Swal from 'sweetalert2';
import { getRoleDayPrefix } from '../../../helper/role-helper';

const STATUS_COLORS = {
  Pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'In Progress': 'bg-blue-100 text-blue-800 border-blue-200',
  Submitted: 'bg-purple-100 text-purple-800 border-purple-200',
  'Under Review': 'bg-amber-100 text-amber-800 border-amber-300',
  'Changes Requested': 'bg-orange-100 text-orange-900 border-orange-300 font-bold',
  Completed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  Rejected: 'bg-red-100 text-red-800 border-red-200',
  Overdue: 'bg-rose-100 text-rose-800 border-rose-300 font-bold',
};

const PRIORITY_COLORS = {
  High: 'bg-rose-50 text-rose-600 border-rose-200',
  Medium: 'bg-amber-50 text-amber-600 border-amber-200',
  Low: 'bg-blue-50 text-blue-600 border-blue-200',
};

const CATEGORY_COLORS = {
  'SEO Content': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Web Development': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'UI/UX Design': 'bg-pink-50 text-pink-700 border-pink-200',
  'Graphic Design': 'bg-purple-50 text-purple-700 border-purple-200',
  'Social Media': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'General': 'bg-gray-50 text-gray-700 border-gray-200',
};

export const cleanFileName = (filename) => {
  if (!filename) return 'Attachment';
  const bare = filename.split('/').pop()?.split('\\').pop() || filename;
  const extMatch = bare.match(/\.([0-9a-z]+)$/i);
  const ext = extMatch ? `.${extMatch[1]}` : '';
  const baseName = extMatch ? bare.slice(0, -ext.length) : bare;
  const withoutTimestamp = baseName.replace(/^\d+[-_]?/, '');
  if (!withoutTimestamp || /^\d+$/.test(withoutTimestamp)) {
    return bare;
  }
  return `${withoutTimestamp}${ext}`;
};

// Helper: extract all URLs from referenceUrl, description, and adminNotes
export const extractAllTaskLinks = (task) => {
  if (!task) return [];
  const foundLinks = [];
  const seenUrls = new Set();

  const addLink = (rawUrl, contextTitle) => {
    if (!rawUrl || typeof rawUrl !== 'string') return;
    let url = rawUrl.trim();
    url = url.replace(/[.,;:)\]]+$/, '');
    if (!url || url.length < 4) return;

    const fullUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
    const lower = fullUrl.toLowerCase();

    if (!lower.includes('.') || seenUrls.has(fullUrl)) return;
    seenUrls.add(fullUrl);

    let type = 'web';
    let label = contextTitle || 'Resource Link';

    if (lower.includes('figma.com')) {
      type = 'figma';
      label = 'Figma Design';
    } else if (lower.includes('github.com')) {
      type = 'github';
      label = 'GitHub Repo';
    } else if (lower.includes('drive.google.com') || lower.includes('docs.google.com')) {
      type = 'google-drive';
      label = 'Google Drive / Doc';
    } else if (lower.includes('loom.com')) {
      type = 'loom';
      label = 'Loom Video';
    } else if (lower.includes('youtube.com') || lower.includes('youtu.be')) {
      type = 'youtube';
      label = 'Video Reference';
    } else if (lower.includes('jira.') || lower.includes('atlassian.net') || lower.includes('linear.app') || lower.includes('trello.com')) {
      type = 'jira';
      label = 'Ticket / Specs';
    }

    foundLinks.push({
      url: fullUrl,
      displayUrl: fullUrl.replace(/^https?:\/\/(www\.)?/, ''),
      type,
      label
    });
  };

  // 1. From referenceUrl field (may contain multiple links separated by comma, space, newline)
  if (task.referenceUrl) {
    const splitUrls = task.referenceUrl.split(/[\s,\n\r]+/);
    splitUrls.forEach(u => addLink(u, 'Reference Link'));
  }

  // 2. From description and adminNotes via URL regex
  const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;
  if (task.description) {
    const matches = task.description.match(urlRegex) || [];
    matches.forEach(u => addLink(u, 'Specification Link'));
  }
  if (task.adminNotes) {
    const matches = task.adminNotes.match(urlRegex) || [];
    matches.forEach(u => addLink(u, 'Mentor Link'));
  }

  return foundLinks;
};

// Helper: auto-linkify URLs inside description text
export const renderLinkifiedText = (text) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (part && (part.startsWith('http://') || part.startsWith('https://') || part.startsWith('www.'))) {
      const full = part.startsWith('http') ? part : `https://${part}`;
      return (
        <a
          key={i}
          href={full}
          target="_blank"
          rel="noreferrer"
          className="text-[#f06600] font-bold underline hover:text-[#d05800] inline-flex items-center gap-0.5 mx-0.5"
          onClick={(e) => e.stopPropagation()}
        >
          {part} <ExternalLink className="w-2.5 h-2.5 inline" />
        </a>
      );
    }
    return part;
  });
};

const DailyTasks = () => {
  const [activeTab, setActiveTab] = useState('today'); // 'today' | 'all'
  const [todayData, setTodayData] = useState(null);
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('All');

  // Submission / Edit modal state
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [activeTask, setActiveTask] = useState(null);
  const [submissionData, setSubmissionData] = useState({
    githubLink: '',
    liveLink: '',
    notes: '',
    updateType: 'Final' // 'Final' marks Submitted, 'Progress' keeps In Progress
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // History viewer inside modal
  const [updateHistory, setUpdateHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Dynamic token getter to guarantee freshness on every request
  const getAuthHeaders = () => {
    let token = localStorage.getItem('portalToken') || localStorage.getItem('adminToken');
    if (!token || token === 'null' || token === 'undefined' || token.startsWith('{') || token.startsWith('[')) {
      token = null;
    }
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchTodayTasks = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${window.API_URL}/api/portal/shared/daily-tasks/today`, { headers });
      setTodayData(res.data);
    } catch (err) {
      console.error("Failed to load today's tasks:", err);
    }
  }, []);

  const fetchAllTasks = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${window.API_URL}/api/portal/shared/tasks`, { headers });
      setAllTasks(res.data);
    } catch (err) {
      console.error("Failed to load all tasks:", err);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchTodayTasks(), fetchAllTasks()]);
    setLoading(false);
  }, [fetchTodayTasks, fetchAllTasks]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const fetchTaskHistory = async (taskId) => {
    setLoadingHistory(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${window.API_URL}/api/portal/shared/tasks/${taskId}/updates`, { headers });
      setUpdateHistory(res.data);
    } catch (err) {
      console.error(err);
      setUpdateHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const openSubmitModal = (task, defaultType = 'Final') => {
    setActiveTask(task);
    setSubmissionData({
      githubLink: task.githubLink || '',
      liveLink: task.liveLink || '',
      notes: task.submissionNotes || '',
      updateType: defaultType
    });
    setFiles([]);
    setShowSubmitModal(true);
    setShowHistory(false);
    fetchTaskHistory(task._id);
  };

  const closeSubmitModal = () => {
    setShowSubmitModal(false);
    setActiveTask(null);
    setFiles([]);
    setUpdateHistory([]);
  };

  const handleStartTask = async (task) => {
    try {
      const headers = getAuthHeaders();
      
      // Optimistic UI update so the interface immediately feels fast and alive
      setTodayData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          tasks: (prev.tasks || []).map(t => t._id === task._id ? { ...t, status: 'In Progress' } : t)
        };
      });
      setAllTasks(prev => prev.map(t => t._id === task._id ? { ...t, status: 'In Progress' } : t));

      let res;
      try {
        // Dedicated start endpoint
        res = await axios.put(`${window.API_URL}/api/portal/shared/tasks/${task._id}/start`, {}, { headers });
      } catch (firstErr) {
        // Fallback to submit endpoint
        res = await axios.put(`${window.API_URL}/api/portal/shared/tasks/${task._id}/submit`, {
          status: 'In Progress'
        }, { headers });
      }

      Swal.fire({
        icon: 'success',
        title: 'Task Started!',
        text: 'Status updated to In Progress. Good luck!',
        toast: true,
        position: 'top-end',
        timer: 2000,
        showConfirmButton: false
      });
      refreshAll();
    } catch (err) {
      console.error("Start task error:", err);
      // Revert optimistic update on failure
      refreshAll();
      const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to start task';
      Swal.fire('Error', errorMsg, 'error');
    }
  };

  const handleSubmitWork = async (e) => {
    e.preventDefault();
    if (!activeTask) return;
    setSubmitting(true);

    try {
      const headers = getAuthHeaders();
      const formData = new FormData();
      formData.append('githubLink', submissionData.githubLink);
      formData.append('liveLink', submissionData.liveLink);
      formData.append('notes', submissionData.notes);
      formData.append('updateNotes', submissionData.notes);
      formData.append('updateType', submissionData.updateType);
      
      // If Final, mark as Submitted; otherwise keep In Progress
      if (submissionData.updateType === 'Final') {
        formData.append('status', 'Submitted');
      } else {
        formData.append('status', 'In Progress');
      }

      Array.from(files).forEach(file => formData.append('files', file));

      // Use submit endpoint to update task status and save files
      await axios.put(
        `${window.API_URL}/api/portal/shared/tasks/${activeTask._id}/submit`,
        formData,
        { headers }
      );

      // Also log progress update if notes provided
      if (submissionData.notes) {
        try {
          const updateData = new FormData();
          updateData.append('updateNotes', submissionData.notes);
          updateData.append('githubLink', submissionData.githubLink);
          updateData.append('liveLink', submissionData.liveLink);
          updateData.append('updateType', submissionData.updateType);
          Array.from(files).forEach(file => updateData.append('files', file));
          await axios.post(
            `${window.API_URL}/api/portal/shared/tasks/${activeTask._id}/update`,
            updateData,
            { headers }
          );
        } catch (err) {
          // non-blocking
        }
      }

      Swal.fire({
        icon: 'success',
        title: submissionData.updateType === 'Final' ? 'Task Submitted for Review!' : 'Progress Logged!',
        text: submissionData.updateType === 'Final'
          ? 'Great job! Your task has been submitted to the admin for review.'
          : 'Your daily progress notes have been saved.',
        confirmButtonColor: '#f06600',
      });

      closeSubmitModal();
      refreshAll();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Submission failed.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const getFileUrl = (filename) => {
    if (!filename) return '#';
    const bare = filename.startsWith('/uploads/') ? filename.replace('/uploads/', '') : filename.replace(/^\/+/, '');
    return `${window.API_URL}/uploads/${bare}`;
  };

  const handleDownloadFile = async (e, filename) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!filename) return;
    const bare = filename.startsWith('/uploads/') ? filename.replace('/uploads/', '') : filename.replace(/^\/+/, '');
    const downloadUrl = `${window.API_URL}/api/portal/shared/files/download/${encodeURIComponent(bare)}`;
    const cleanName = cleanFileName(bare);

    try {
      const res = await axios.get(downloadUrl, {
        responseType: 'blob',
        headers: getAuthHeaders()
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.setAttribute('download', cleanName || bare);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.warn("Direct blob download failed, falling back to direct URL:", err);
      window.open(getFileUrl(filename), '_blank');
    }
  };

  const todayTasks = todayData?.tasks || [];
  const metrics = todayData?.metrics || {
    totalTasks: todayTasks.length,
    completedTasks: todayTasks.filter(t => t.status === 'Completed').length,
    completionRate: todayTasks.length > 0 ? Math.round((todayTasks.filter(t => t.status === 'Completed').length / todayTasks.length) * 100) : 0,
    dayStatus: 'No Assignment'
  };

  let currentUser = todayData?.user;
  if (!currentUser) {
    try {
      currentUser = JSON.parse(localStorage.getItem('portalUser')) || {};
    } catch (e) {
      currentUser = {};
    }
  }
  const roleDayPrefix = getRoleDayPrefix(currentUser);

  const filteredAllTasks = allTasks.filter(t => {
    if (filterStatus === 'All') return true;
    if (filterStatus === 'Overdue') {
      const isPast = t.deadline && new Date(t.deadline) < new Date();
      return (isPast && t.status !== 'Completed') || t.status === 'Overdue';
    }
    return t.status === filterStatus;
  });

  return (
    <div className="space-y-6 text-gray-900 pb-16">
      {/* Page Title & Refresh */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-extrabold tracking-tight">My Tasks & Daily Assignments</h2>
          <p className="text-gray-500 text-sm mt-1">
            Focus on today's assigned tasks. Complete each task to automatically earn your daily attendance.
          </p>
        </div>
        <button
          onClick={refreshAll}
          disabled={loading}
          className="self-start sm:self-auto flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 text-gray-700 font-semibold text-sm transition-all shadow-sm"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-[#f06600]' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex bg-white border border-gray-200 p-1.5 rounded-2xl shadow-sm">
        <button
          onClick={() => setActiveTab('today')}
          className={`flex-1 py-3 px-6 font-bold text-sm rounded-xl transition-all flex items-center justify-center gap-2 ${
            activeTab === 'today'
              ? 'bg-[#f06600] text-white shadow-sm'
              : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
          }`}
        >
          <Sparkles className="w-4 h-4" />
          Today's Tasks {todayData?.internshipDay ? `(${roleDayPrefix} Day ${todayData.internshipDay})` : ''}
          {todayTasks.length > 0 && (
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
              activeTab === 'today' ? 'bg-white text-[#f06600]' : 'bg-[#f06600]/10 text-[#f06600]'
            }`}>
              {todayTasks.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`flex-1 py-3 px-6 font-bold text-sm rounded-xl transition-all flex items-center justify-center gap-2 ${
            activeTab === 'all'
              ? 'bg-[#f06600] text-white shadow-sm'
              : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
          }`}
        >
          <Layers className="w-4 h-4" />
          All & Past Tasks ({allTasks.length})
        </button>
      </div>

      {/* ========================================================================= */}
      {/* TAB 1: TODAY'S TASKS (Day X Priority View) */}
      {/* ========================================================================= */}
      {activeTab === 'today' && (
        <div className="space-y-6">
          {/* Today's Hero Banner with Progress & Attendance Status */}
          <div className="bg-gradient-to-br from-[#f06600]/10 via-amber-50 to-white rounded-3xl p-6 sm:p-8 border border-[#f06600]/20 shadow-sm">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="space-y-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="bg-[#f06600] text-white text-xs uppercase font-extrabold px-3 py-1 rounded-full shadow-sm tracking-wide">
                    {todayData?.internshipDay ? `${roleDayPrefix} Day ${todayData.internshipDay}` : "Today's Agenda"}
                  </span>
                  <span className="text-sm font-semibold text-gray-600">
                    {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>

                <h3 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">
                  {todayTasks.length === 0
                    ? 'No Tasks Assigned For Today'
                    : `You have ${todayTasks.length} Task${todayTasks.length !== 1 ? 's' : ''} to complete`}
                </h3>

                <p className="text-sm text-gray-600 max-w-2xl leading-relaxed">
                  {todayTasks.length === 0
                    ? 'Check back later or review past tasks in the All Tasks tab.'
                    : 'Beta Communes daily attendance is automatically marked Present once all of your assigned daily tasks are completed.'}
                </p>
              </div>

              {/* Attendance Status Card */}
              <div className="bg-white/80 backdrop-blur-md rounded-2xl p-5 border border-gray-200/80 shadow-sm flex flex-col sm:flex-row lg:flex-col gap-4 min-w-[280px]">
                <div>
                  <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
                    Daily Attendance
                  </span>
                  <div className="flex items-center gap-2">
                    {todayData?.attendance?.status === 'Present' ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Present
                      </span>
                    ) : todayData?.attendance?.source === 'admin_override' ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-900 border border-amber-300">
                        <ShieldAlert className="w-4 h-4 text-amber-600" /> Overridden: {todayData.attendance.status}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800 border border-yellow-300">
                        <Clock className="w-4 h-4 text-yellow-600" /> Incomplete / Pending
                      </span>
                    )}
                  </div>
                  {todayData?.attendance?.overrideReason && (
                    <p className="text-[11px] text-gray-500 italic mt-1.5">
                      "{todayData.attendance.overrideReason}"
                    </p>
                  )}
                </div>

                <div className="border-t sm:border-t-0 lg:border-t border-gray-100 pt-3 sm:pt-0 lg:pt-3">
                  <div className="flex justify-between items-center text-xs font-bold text-gray-700 mb-1.5">
                    <span>Today's Progress</span>
                    <span className="text-[#f06600]">
                      {metrics.completedTasks} / {metrics.totalTasks} ({metrics.completionRate}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${
                        metrics.completionRate === 100
                          ? 'bg-gradient-to-r from-emerald-500 to-green-500'
                          : 'bg-gradient-to-r from-[#f06600] to-amber-500'
                      }`}
                      style={{ width: `${metrics.completionRate}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Today's Tasks Grid */}
          {todayTasks.length === 0 ? (
            <div className="bg-white rounded-3xl border border-gray-200 p-12 text-center shadow-sm">
              <Sparkles className="w-12 h-12 text-amber-400 mx-auto mb-3 opacity-60" />
              <h4 className="text-xl font-bold text-gray-900 mb-1">All Caught Up for Today!</h4>
              <p className="text-sm text-gray-500 max-w-md mx-auto mb-4">
                You do not have any tasks assigned for today. Feel free to browse through past tasks or check in with your mentor.
              </p>
              <button
                onClick={() => setActiveTab('all')}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#f06600] hover:bg-[#d05800] text-white rounded-xl font-bold text-sm transition-all shadow-sm"
              >
                View All Assigned Tasks <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {todayTasks.map((task, idx) => (
                <TaskCard
                  key={task._id}
                  task={task}
                  index={idx}
                  onStart={() => handleStartTask(task)}
                  onSubmit={() => openSubmitModal(task, 'Final')}
                  onUpdate={() => openSubmitModal(task, 'Progress')}
                  getFileUrl={getFileUrl}
                  handleDownloadFile={handleDownloadFile}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: ALL & PAST TASKS */}
      {/* ========================================================================= */}
      {activeTab === 'all' && (
        <div className="space-y-6">
          {/* Filter Bar */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm flex flex-wrap justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Status:</span>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-gray-800 outline-none focus:ring-2 focus:ring-[#f06600]"
              >
                <option value="All">All Tasks ({allTasks.length})</option>
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Submitted">Submitted</option>
                <option value="Under Review">Under Review</option>
                <option value="Changes Requested">Changes Requested</option>
                <option value="Completed">Completed</option>
                <option value="Overdue">Overdue</option>
              </select>
            </div>

            <span className="text-xs text-gray-500">
              Showing <strong className="text-gray-900">{filteredAllTasks.length}</strong> of {allTasks.length} tasks
            </span>
          </div>

          {/* All Tasks Grid */}
          {filteredAllTasks.length === 0 ? (
            <div className="bg-white rounded-3xl border border-gray-200 p-12 text-center text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p className="text-base font-bold text-gray-700">No tasks match this filter</p>
              <p className="text-xs text-gray-500 mt-1">Try switching filter back to "All Tasks".</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredAllTasks.map((task, idx) => (
                <TaskCard
                  key={task._id}
                  task={task}
                  index={idx}
                  compact
                  onStart={() => handleStartTask(task)}
                  onSubmit={() => openSubmitModal(task, 'Final')}
                  onUpdate={() => openSubmitModal(task, 'Progress')}
                  getFileUrl={getFileUrl}
                  handleDownloadFile={handleDownloadFile}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* INDIVIDUAL TASK SUBMISSION MODAL */}
      {/* ========================================================================= */}
      {showSubmitModal && activeTask && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full border border-gray-200 my-8 overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#f06600]/10 to-amber-50 px-6 py-5 border-b border-gray-200/80 flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {activeTask.internshipDay && (
                    <span className="bg-[#f06600] text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full">
                      {roleDayPrefix} Day {activeTask.internshipDay}
                    </span>
                  )}
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${CATEGORY_COLORS[activeTask.category] || 'bg-gray-100 text-gray-700'}`}>
                    {activeTask.category || 'General'}
                  </span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_COLORS[activeTask.status] || 'bg-gray-100'}`}>
                    {activeTask.status}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">{activeTask.title}</h3>
              </div>
              <button
                onClick={closeSubmitModal}
                className="text-gray-400 hover:text-gray-700 p-1.5 rounded-xl hover:bg-white/60 transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
              {/* Revision Notice Alert if Changes Requested */}
              {activeTask.status === 'Changes Requested' && activeTask.feedback && (
                <div className="bg-orange-50 border-2 border-orange-300 rounded-2xl p-4 text-orange-950">
                  <div className="flex items-center gap-2 font-bold text-sm mb-1 text-orange-900">
                    <AlertTriangle className="w-4 h-4 text-orange-600" />
                    Admin Feedback — Revision Required:
                  </div>
                  <p className="text-xs sm:text-sm whitespace-pre-wrap leading-relaxed">
                    {activeTask.feedback}
                  </p>
                </div>
              )}

              {/* Task Details Summary */}
              <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100 text-xs space-y-2">
                <p className="text-gray-700 text-sm whitespace-pre-wrap">{activeTask.description}</p>
                
                {activeTask.expectedOutput && (
                  <div className="pt-2 border-t border-gray-200">
                    <span className="font-bold text-gray-700">Expected Deliverable:</span> {activeTask.expectedOutput}
                  </div>
                )}
                {activeTask.blogTitle && (
                  <div><span className="font-bold text-gray-700">Topic:</span> {activeTask.blogTitle}</div>
                )}
                {activeTask.keywords && (
                  <div><span className="font-bold text-gray-700">Keywords:</span> {activeTask.keywords}</div>
                )}
                {activeTask.wordCount && (
                  <div><span className="font-bold text-gray-700">Target Word Count:</span> {activeTask.wordCount} words</div>
                )}
                {/* Extracted Links in Modal */}
                {(() => {
                  const modalLinks = extractAllTaskLinks(activeTask);
                  if (modalLinks.length === 0) return null;
                  return (
                    <div className="space-y-1.5 pt-1">
                      <span className="font-bold text-gray-700 block text-xs">Reference Links & Resources ({modalLinks.length}):</span>
                      <div className="space-y-1.5">
                        {modalLinks.map((item, lIdx) => (
                          <div key={lIdx} className="flex items-center justify-between gap-3 bg-purple-50/80 border border-purple-200/90 text-purple-950 p-2.5 rounded-xl text-xs">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              {item.type === 'figma' ? (
                                <SiFigma className="w-4 h-4 text-purple-600 shrink-0" />
                              ) : item.type === 'github' ? (
                                <FaGithub className="w-4 h-4 text-gray-800 shrink-0" />
                              ) : item.type === 'google-drive' ? (
                                <SiGoogledrive className="w-4 h-4 text-emerald-600 shrink-0" />
                              ) : item.type === 'jira' ? (
                                <SiJira className="w-4 h-4 text-blue-600 shrink-0" />
                              ) : item.type === 'loom' || item.type === 'youtube' ? (
                                <Video className="w-4 h-4 text-rose-600 shrink-0" />
                              ) : (
                                <Globe className="w-4 h-4 text-[#f06600] shrink-0" />
                              )}
                              <span className="font-bold text-gray-900 shrink-0">{item.label}:</span>
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[#f06600] hover:underline truncate"
                              >
                                {item.displayUrl}
                              </a>
                            </div>
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noreferrer"
                              className="px-2.5 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-[10px] font-bold shrink-0 flex items-center gap-1 shadow-2xs"
                            >
                              Open <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {activeTask.adminNotes && (
                  <div className="bg-amber-50/60 p-2.5 rounded-xl border border-amber-200/60 text-amber-900 mt-2">
                    <span className="font-bold">Mentor Notes:</span> {renderLinkifiedText(activeTask.adminNotes)}
                  </div>
                )}

                {activeTask.attachments?.length > 0 && (
                  <div className="bg-orange-50/70 p-3 rounded-xl border border-orange-200 text-orange-950 mt-2 space-y-2">
                    <span className="font-bold text-xs text-orange-900 flex items-center gap-1.5">
                      <Paperclip className="w-3.5 h-3.5 text-[#f06600]" /> Mentor Shared Files ({activeTask.attachments.length}):
                    </span>
                    <div className="space-y-1.5">
                      {activeTask.attachments.map((f, i) => {
                        const displayName = cleanFileName(f);
                        return (
                          <div
                            key={i}
                            className="flex items-center justify-between gap-2 p-2 bg-white rounded-lg border border-orange-200/80 shadow-2xs text-xs"
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <FileText className="w-3.5 h-3.5 text-[#f06600] shrink-0" />
                              <span className="font-semibold text-gray-800 truncate" title={f}>
                                {displayName}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <button
                                type="button"
                                onClick={(e) => handleDownloadFile(e, f)}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#f06600] hover:bg-[#d05800] text-white rounded-md text-[10px] font-bold cursor-pointer transition-colors"
                              >
                                <Download className="w-3 h-3" /> Download
                              </button>
                              <a
                                href={getFileUrl(f)}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-[10px] font-medium"
                              >
                                <ExternalLink className="w-3 h-3" /> View
                              </a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Submission Form */}
              <form onSubmit={handleSubmitWork} className="space-y-4">
                {/* Mode Selector: Final Submission vs Progress Update */}
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1.5">
                    Action Type
                  </label>
                  <div className="grid grid-cols-2 gap-2 bg-gray-100 p-1 rounded-2xl">
                    <button
                      type="button"
                      onClick={() => setSubmissionData({ ...submissionData, updateType: 'Final' })}
                      className={`py-2 px-3 rounded-xl text-xs font-bold transition-all ${
                        submissionData.updateType === 'Final'
                          ? 'bg-[#f06600] text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      🏁 Final Submission (Ready for Review)
                    </button>
                    <button
                      type="button"
                      onClick={() => setSubmissionData({ ...submissionData, updateType: 'Progress' })}
                      className={`py-2 px-3 rounded-xl text-xs font-bold transition-all ${
                        submissionData.updateType === 'Progress'
                          ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      📋 Daily Progress Notes
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1">
                    {submissionData.updateType === 'Final'
                      ? 'Submits this task to the admin. Marked as Submitted.'
                      : 'Logs today\'s ongoing progress while keeping task In Progress.'}
                  </p>
                </div>

                {/* Live / Preview Link */}
                <div>
                  <label className="text-xs font-bold text-gray-600 block mb-1">
                    Live Demo / Published Link / Google Drive (Optional)
                  </label>
                  <input
                    type="url"
                    placeholder="https://your-preview-or-deliverable.com"
                    value={submissionData.liveLink}
                    onChange={e => setSubmissionData({ ...submissionData, liveLink: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600]"
                  />
                </div>

                {/* GitHub / Repo Link */}
                <div>
                  <label className="text-xs font-bold text-gray-600 block mb-1">
                    GitHub / Figma / Repository Link (Optional)
                  </label>
                  <input
                    type="url"
                    placeholder="https://github.com/org/repo or Figma link"
                    value={submissionData.githubLink}
                    onChange={e => setSubmissionData({ ...submissionData, githubLink: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600]"
                  />
                </div>

                {/* Submission Notes */}
                <div>
                  <label className="text-xs font-bold text-gray-600 block mb-1">
                    Submission Notes & Summary *
                  </label>
                  <textarea
                    required
                    placeholder="Describe your work, completed features, word count, or instructions for the reviewer..."
                    value={submissionData.notes}
                    onChange={e => setSubmissionData({ ...submissionData, notes: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600] h-28 resize-none"
                  />
                </div>

                {/* Deliverables & Files Upload */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-700 block">
                    Upload Deliverable Files / Screenshots / Documents
                  </label>
                  <div className="relative border-2 border-dashed border-gray-200 hover:border-[#f06600]/60 rounded-2xl p-4 bg-gray-50 text-center transition-all cursor-pointer group">
                    <input
                      type="file"
                      multiple
                      id="task-file-input"
                      onChange={e => {
                        const newFiles = Array.from(e.target.files);
                        setFiles(prev => [...Array.from(prev || []), ...newFiles]);
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="flex flex-col items-center justify-center gap-1.5 pointer-events-none">
                      <div className="w-9 h-9 rounded-xl bg-[#f06600]/10 text-[#f06600] flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Upload className="w-4 h-4" />
                      </div>
                      <p className="text-xs font-bold text-gray-800">
                        Click or drag files here to attach
                      </p>
                      <p className="text-[10px] text-gray-400">
                        Supports images, PDFs, ZIPs, documents, and code files
                      </p>
                    </div>
                  </div>

                  {/* Staged files preview list */}
                  {files && files.length > 0 && (
                    <div className="space-y-1.5 pt-1">
                      <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block">
                        Files ready to upload ({files.length}):
                      </span>
                      <div className="space-y-1">
                        {Array.from(files).map((file, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-2.5 bg-white rounded-xl border border-gray-200 text-xs shadow-2xs"
                          >
                            <div className="flex items-center gap-2 truncate min-w-0">
                              <Paperclip className="w-3.5 h-3.5 text-[#f06600] shrink-0" />
                              <span className="font-semibold text-gray-800 truncate">{file.name}</span>
                              <span className="text-[10px] text-gray-400 shrink-0">
                                ({(file.size / 1024).toFixed(1)} KB)
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setFiles(prev => Array.from(prev).filter((_, i) => i !== idx))}
                              className="text-gray-400 hover:text-red-500 p-1 rounded transition-colors shrink-0 text-sm font-bold"
                              title="Remove file"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Existing attachments from past submissions */}
                {activeTask.submittedFiles?.length > 0 && (
                  <div className="pt-2">
                    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">
                      Previously Uploaded Files ({activeTask.submittedFiles.length})
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {activeTask.submittedFiles.map((f, i) => (
                        <a
                          key={i}
                          href={getFileUrl(f)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold"
                        >
                          <Paperclip className="w-3 h-3 text-[#f06600]" />
                          {f.split('/').pop()}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Modal Actions */}
                <div className="flex gap-3 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={closeSubmitModal}
                    className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold text-sm transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 py-3 px-4 bg-[#f06600] hover:bg-[#d05800] text-white rounded-xl font-bold text-sm transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <RotateCcw className="w-4 h-4 animate-spin" /> Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        {submissionData.updateType === 'Final' ? 'Submit Task For Review' : 'Save Progress'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =========================================================================
// HELPER COMPONENT: TaskCard
// =========================================================================
const TaskCard = ({ task, index, compact, onStart, onSubmit, onUpdate, getFileUrl, handleDownloadFile }) => {
  const [expandedDesc, setExpandedDesc] = useState(false);
  const isCompleted = task.status === 'Completed';
  const isSubmitted = task.status === 'Submitted' || task.status === 'Under Review';
  const isChangesRequested = task.status === 'Changes Requested';
  const isPending = task.status === 'Pending';
  const isInProgress = task.status === 'In Progress';

  const cardLinks = extractAllTaskLinks(task);
  const cardRolePrefix = getRoleDayPrefix(task.assignedTo);

  return (
    <div className={`bg-white rounded-3xl border transition-all duration-200 flex flex-col ${
      isChangesRequested
        ? 'border-orange-300 shadow-md ring-2 ring-orange-200/50'
        : isCompleted
        ? 'border-emerald-200 shadow-sm'
        : 'border-gray-200 shadow-sm hover:shadow-md hover:border-[#f06600]/30'
    } p-5 sm:p-6`}>
      {/* Top Badges */}
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {task.project && (
            <span className="bg-blue-50 text-blue-700 border border-blue-200 text-[10px] font-extrabold px-2.5 py-0.5 rounded-full flex items-center gap-1">
              <Briefcase className="w-3 h-3 text-blue-600" />
              Project: {typeof task.project === 'object' ? task.project.title : 'Client Project'}
              {typeof task.project === 'object' && task.project.clientName && (
                <span className="text-blue-500 font-medium">({task.project.clientName})</span>
              )}
            </span>
          )}
          {task.internshipDay && (
            <span className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px] font-extrabold px-2.5 py-0.5 rounded-full">
              {cardRolePrefix} Day {task.internshipDay}
            </span>
          )}
          <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${CATEGORY_COLORS[task.category] || 'bg-gray-100 text-gray-700'}`}>
            {task.category || 'General'}
          </span>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${PRIORITY_COLORS[task.priority] || 'bg-gray-100 text-gray-700'}`}>
            {task.priority}
          </span>
        </div>

        <span className={`text-xs font-bold px-3 py-1 rounded-full border ${STATUS_COLORS[task.status] || 'bg-gray-100'}`}>
          {task.status}
        </span>
      </div>

      {/* Task Title */}
      <h4 className="text-lg font-bold text-gray-900 mb-2 leading-tight">
        {task.title}
      </h4>

      {/* Description with auto-linkification and expand support */}
      <div className="text-sm text-gray-600 mb-4 leading-relaxed flex-1">
        <div className={expandedDesc ? '' : 'line-clamp-3'}>
          {renderLinkifiedText(task.description)}
        </div>
        {task.description && task.description.length > 160 && (
          <button
            type="button"
            onClick={() => setExpandedDesc(!expandedDesc)}
            className="text-[11px] font-bold text-[#f06600] hover:text-[#d05800] mt-1 inline-flex items-center gap-0.5"
          >
            {expandedDesc ? 'Show less' : 'Show full details...'}
          </button>
        )}
      </div>

      {/* Category-Specific Details Pill */}
      {(task.blogTitle || task.keywords || task.wordCount || task.expectedOutput) && (
        <div className="bg-gray-50 rounded-2xl p-3 border border-gray-100 text-xs text-gray-700 space-y-1 mb-4">
          {task.expectedOutput && (
            <div><span className="font-bold text-gray-900">Deliverable:</span> {task.expectedOutput}</div>
          )}
          {task.blogTitle && (
            <div><span className="font-bold text-gray-900">Topic:</span> {task.blogTitle}</div>
          )}
          {task.keywords && (
            <div><span className="font-bold text-gray-900">Keywords:</span> {task.keywords}</div>
          )}
          {task.wordCount && (
            <div><span className="font-bold text-gray-900">Word Count:</span> {task.wordCount} words</div>
          )}
        </div>
      )}

      {/* Changes Requested Banner */}
      {isChangesRequested && task.feedback && (
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-3.5 mb-4 text-xs text-orange-900">
          <div className="flex items-center gap-1.5 font-bold mb-1 text-orange-800">
            <AlertTriangle className="w-3.5 h-3.5 text-orange-600" />
            Revision Required:
          </div>
          <p className="leading-relaxed font-medium">{task.feedback}</p>
        </div>
      )}

      {/* Admin Notes if provided */}
      {task.adminNotes && (
        <div className="bg-amber-50/80 border border-amber-200 rounded-2xl p-3 text-xs text-amber-950 mb-3 leading-relaxed">
          <span className="font-bold text-amber-800">Admin Note:</span> {renderLinkifiedText(task.adminNotes)}
        </div>
      )}

      {/* Reference Links & Resources (Figma, GitHub, Drive, Docs, Jira, Loom) */}
      {cardLinks.length > 0 && (
        <div className="mb-4 space-y-1.5">
          <div className="text-[11px] font-bold text-gray-700 flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5 text-[#f06600]" /> Reference &amp; Resource Links ({cardLinks.length}):
          </div>
          <div className="space-y-1.5">
            {cardLinks.map((linkItem, lIdx) => (
              <a
                key={lIdx}
                href={linkItem.url}
                target="_blank"
                rel="noreferrer"
                className={`flex items-center justify-between gap-2.5 p-2.5 rounded-2xl border text-xs font-semibold transition-all hover:scale-[1.008] shadow-2xs ${
                  linkItem.type === 'figma'
                    ? 'bg-purple-50/90 border-purple-200 text-purple-900 hover:bg-purple-100 hover:border-purple-300'
                    : linkItem.type === 'github'
                    ? 'bg-slate-50 border-slate-300 text-slate-900 hover:bg-slate-100'
                    : linkItem.type === 'google-drive'
                    ? 'bg-emerald-50/90 border-emerald-200 text-emerald-900 hover:bg-emerald-100'
                    : linkItem.type === 'youtube' || linkItem.type === 'loom'
                    ? 'bg-rose-50/90 border-rose-200 text-rose-900 hover:bg-rose-100'
                    : 'bg-orange-50/80 border-orange-200 text-orange-950 hover:bg-orange-100'
                }`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className={`p-1.5 rounded-xl shrink-0 ${
                    linkItem.type === 'figma'
                      ? 'bg-purple-200 text-purple-800'
                      : linkItem.type === 'github'
                      ? 'bg-slate-800 text-white'
                      : linkItem.type === 'google-drive'
                      ? 'bg-emerald-200 text-emerald-800'
                      : linkItem.type === 'youtube' || linkItem.type === 'loom'
                      ? 'bg-rose-200 text-rose-800'
                      : 'bg-orange-200 text-[#f06600]'
                  }`}>
                    {linkItem.type === 'figma' && <SiFigma className="w-3.5 h-3.5" />}
                    {linkItem.type === 'github' && <FaGithub className="w-3.5 h-3.5" />}
                    {linkItem.type === 'google-drive' && <SiGoogledrive className="w-3.5 h-3.5" />}
                    {linkItem.type === 'jira' && <SiJira className="w-3.5 h-3.5" />}
                    {(linkItem.type === 'youtube' || linkItem.type === 'loom') && <Video className="w-3.5 h-3.5" />}
                    {linkItem.type === 'web' && <ExternalLink className="w-3.5 h-3.5" />}
                  </div>
                  <div className="truncate">
                    <span className="block text-[11px] font-bold leading-tight truncate">
                      {linkItem.label}
                    </span>
                    <span className="block text-[10px] font-normal text-gray-500 truncate max-w-[200px] sm:max-w-xs">
                      {linkItem.displayUrl}
                    </span>
                  </div>
                </div>

                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-xl shrink-0 flex items-center gap-1 shadow-2xs transition-colors ${
                  linkItem.type === 'figma'
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : linkItem.type === 'github'
                    ? 'bg-slate-900 text-white hover:bg-slate-800'
                    : 'bg-[#f06600] text-white hover:bg-[#d05800]'
                }`}>
                  Open <ExternalLink className="w-2.5 h-2.5" />
                </span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Submitted Deliverables & Files on Card */}
      {task.submittedFiles?.length > 0 && (
        <div className="bg-purple-50/80 border border-purple-200/90 rounded-2xl p-3 mb-4 text-xs">
          <div className="flex items-center justify-between gap-2 mb-2 font-bold text-purple-950">
            <span className="flex items-center gap-1.5">
              <Upload className="w-3.5 h-3.5 text-purple-600" /> Submitted Deliverables ({task.submittedFiles.length}):
            </span>
          </div>
          <div className="space-y-1.5">
            {task.submittedFiles.map((file, idx) => {
              const displayName = cleanFileName(file);
              return (
                <div
                  key={idx}
                  className="flex items-center justify-between gap-2 p-2 bg-white border border-purple-200/70 rounded-xl shadow-2xs hover:border-purple-300 transition-all"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FileText className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                    <span className="font-semibold text-gray-800 text-[11px] truncate" title={file}>
                      {displayName}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={(e) => handleDownloadFile ? handleDownloadFile(e, file) : window.open(getFileUrl(file), '_blank')}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-[10px] font-bold transition-all shadow-xs cursor-pointer"
                      title="Download deliverable"
                    >
                      <Download className="w-3 h-3" /> Download
                    </button>
                    <a
                      href={getFileUrl(file)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-[10px] font-semibold transition-all"
                      title="View file"
                    >
                      <ExternalLink className="w-3 h-3" /> View
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Submitted Links on Card */}
      {(task.githubLink || task.liveLink) && (
        <div className="flex flex-wrap gap-2 mb-4 text-xs">
          {task.githubLink && (
            <a
              href={task.githubLink.startsWith('http') ? task.githubLink : `https://${task.githubLink}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 hover:bg-gray-800 text-white rounded-xl text-xs font-bold transition-all shadow-sm"
            >
              <FaGithub className="w-3.5 h-3.5" /> Repository
            </a>
          )}
          {task.liveLink && (
            <a
              href={task.liveLink.startsWith('http') ? task.liveLink : `https://${task.liveLink}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Live Demo
            </a>
          )}
        </div>
      )}

      {/* Mentor Shared Files / Attachments */}
      {task.attachments?.length > 0 && (
        <div className="bg-orange-50/80 border border-orange-200/90 rounded-2xl p-3 mb-4 text-xs">
          <div className="flex items-center justify-between gap-2 mb-2 font-bold text-orange-950">
            <span className="flex items-center gap-1.5">
              <Paperclip className="w-3.5 h-3.5 text-[#f06600]" /> Mentor Attachments ({task.attachments.length}):
            </span>
          </div>
          <div className="space-y-1.5">
            {task.attachments.map((file, idx) => {
              const displayName = cleanFileName(file);
              return (
                <div
                  key={idx}
                  className="flex items-center justify-between gap-2 p-2 bg-white border border-orange-200/70 rounded-xl shadow-2xs hover:border-orange-300 transition-all"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div className="p-1 bg-orange-100 text-[#f06600] rounded-md shrink-0">
                      <FileText className="w-3.5 h-3.5" />
                    </div>
                    <span className="font-semibold text-gray-800 text-[11px] truncate" title={file}>
                      {displayName}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={(e) => handleDownloadFile ? handleDownloadFile(e, file) : window.open(getFileUrl(file), '_blank')}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#f06600] hover:bg-[#d05800] text-white rounded-lg text-[10px] font-bold transition-all shadow-xs cursor-pointer"
                      title="Download file directly"
                    >
                      <Download className="w-3 h-3" /> Download
                    </button>
                    <a
                      href={getFileUrl(file)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-[10px] font-semibold transition-all"
                      title="Open in new tab"
                    >
                      <ExternalLink className="w-3 h-3" /> View
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Attachments / Meta */}
      <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-500 py-3 border-t border-gray-100 mb-4">
        <div className="flex items-center gap-1 text-gray-600">
          <Clock className="w-3.5 h-3.5" />
          <span>Deadline: {task.deadline ? new Date(task.deadline).toLocaleDateString() : 'Today'}</span>
        </div>

        {task.attachments?.length > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (task.attachments[0]) {
                handleDownloadFile ? handleDownloadFile(e, task.attachments[0]) : window.open(getFileUrl(task.attachments[0]), '_blank');
              }
            }}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 hover:bg-orange-100 border border-orange-200 text-[#f06600] hover:text-[#d05800] font-bold rounded-lg text-xs transition-colors cursor-pointer shadow-2xs"
            title="Click to download attachment"
          >
            <Paperclip className="w-3.5 h-3.5" /> {task.attachments.length} attachment(s)
            <Download className="w-3 h-3 ml-0.5" />
          </button>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1">
        {isPending && (
          <button
            onClick={onStart}
            className="flex-1 py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs transition-all flex items-center justify-center gap-1.5 shadow-sm active:scale-95 cursor-pointer"
          >
            <Play className="w-3.5 h-3.5 fill-white" /> Start Task
          </button>
        )}

        {isInProgress && (
          <>
            <button
              onClick={onUpdate}
              className="py-2.5 px-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold text-xs transition-all cursor-pointer"
              title="Post progress update without final submit"
            >
              Log Progress
            </button>
            <button
              onClick={onSubmit}
              className="flex-1 py-2.5 px-4 bg-[#f06600] hover:bg-[#d05800] text-white rounded-xl font-bold text-xs transition-all shadow-sm flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <Send className="w-3.5 h-3.5" /> Submit Work
            </button>
          </>
        )}

        {isChangesRequested && (
          <button
            onClick={onSubmit}
            className="flex-1 py-2.5 px-4 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white rounded-xl font-bold text-xs transition-all shadow-sm flex items-center justify-center gap-1.5 cursor-pointer"
          >
            <Send className="w-3.5 h-3.5" /> Resubmit Revised Work
          </button>
        )}

        {isSubmitted && (
          <div className="flex-1 py-2.5 px-4 bg-purple-50 text-purple-700 border border-purple-200 rounded-xl font-bold text-xs text-center">
            Submitted — Under Mentor Review
          </div>
        )}

        {isCompleted && (
          <div className="flex-1 py-2.5 px-4 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl font-bold text-xs text-center flex items-center justify-center gap-1.5">
            <CheckCircle className="w-3.5 h-3.5" /> Task Completed & Approved
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyTasks;
